import { useCallback, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button, PageHeader, SectionCard, Segmented } from '@/components/ui';
import { useConsoleBreadcrumb } from '@/components/shell/ConsoleBreadcrumbContext';
import { useRoleMapTable } from './roleMap/useRoleMapTable';
import { useRoleCoverage } from './roleCoverage/useRoleCoverage';
import { RoleMapBoardView } from './roleMap/RoleMapBoardView';
import { RoleMapListView } from './roleMap/RoleMapListView';

type RoleMapView = 'board' | 'list';

/**
 * EAA-1841: `/console/role-map` — "Liên kết vai trò", now the ONE screen for
 * role links, with two view modes.
 *
 * WHY THIS MERGED. `/console/role-coverage` and `/console/role-map` were built
 * by two tickets from two design frames and shipped as separate routes, but they
 * were never separate screens: frames 42 and 49 carry the same title ("Liên kết
 * vai trò"), the same rows, the same "Phạm vi" concept, and both write the same
 * `RoleMap` endpoints. `docs/design/handoff/UX-SPEC.md` says it outright —
 * "/console/role-map — the only view". The split also forced a glossary
 * violation: to tell the two nav items apart, this route had been relabeled
 * "Quy đổi vai trò", a term that same handoff explicitly bans (§Vietnamese
 * glossary: "Quy đổi" implies the standard role is spent). Merging restores the
 * one allowed label.
 *
 * WHAT EACH VIEW IS FOR — they are genuinely different questions, which is why
 * both survive as views rather than one being deleted:
 *   - **Bảng phủ** (frame 42) answers "where are the GAPS?". Its matrix has a
 *     cell for every app x standard-role pair, so an unlinked pair is visible as
 *     an empty cell. A list of existing links cannot show absence.
 *   - **Danh sách** (frame 49) answers "operate on the links that exist" —
 *     sort, filter, select rows, bulk re-scope/re-sync/delete, export CSV.
 *
 * SCOPE DIFFERS BY VIEW, and that is a backend constraint. The board reads
 * `GET /role-map/coverage`, which filters `organization__isnull=True` and takes
 * no organization param, so it is ecosystem-default only. The list's "Phạm vi"
 * control lives in its own toolbar and is unavailable in the board — the
 * subtitle says which slice is on screen instead of showing a dead control.
 *
 * Only the active view's queries run (see the `active` flag both hooks take);
 * the three query keys they share resolve from one cache entry either way.
 *
 * EAA-1817 (2026-08-03): page chrome rebuilt onto the standard console layout
 * (`PageHeader` + `portal-console-page--fit` + `.fit-grow`) — this used to wrap
 * everything, including the title itself, in one bespoke `.rm-card`, which had
 * no breadcrumb and did not fit the shell (missing `--fit`, so this page grew
 * the whole document instead of scrolling internally, unlike every other
 * console page). See `PermissionsPage.tsx` for the pattern this now matches:
 * breadcrumb + title + actions live in `PageHeader`, OUTSIDE any card. The
 * board (a multi-card layout — rail + coverage matrix + editor, each with its
 * own ring) gets a bare `.fit-grow`; the list (one table) gets `.fit-grow` +
 * a single `SectionCard`.
 */
export function RoleMapPage() {
    const { t } = useTranslation();
    const [searchParams, setSearchParams] = useSearchParams();

    // EAA-1845: view options moved from module scope into the component body
    // because the labels now come through `t()`, which needs the hook above.
    const VIEW_OPTIONS = [
        { label: t('console.roleMap.page.viewBoard'), value: 'board' },
        { label: t('console.roleMap.page.viewList'), value: 'list' },
    ];

    // EAA-1841: the view rides in `?view=` so a particular view is a shareable
    // link — which is also what keeps the old `/console/role-coverage` deep
    // links working after the redirect in `App.tsx`.
    const view: RoleMapView = searchParams.get('view') === 'list' ? 'list' : 'board';

    const listVm = useRoleMapTable({ active: view === 'list' });
    const boardVm = useRoleCoverage(view === 'board');

    const setView = useCallback(
        (next: RoleMapView) => {
            setSearchParams(
                (prev) => {
                    const params = new URLSearchParams(prev);
                    params.set('view', next);
                    return params;
                },
                { replace: true }
            );
        },
        [setSearchParams]
    );

    // EAA-1837: `?application=` deep link from `apps/AppsTable.tsx` seeds the
    // list's "Ứng dụng" filter. It targets a filter that only the list view has,
    // so it also selects that view rather than silently doing nothing on the
    // board.
    const deepLinkApp = searchParams.get('application');
    const { apps, toggleApp } = listVm;
    useEffect(() => {
        if (!deepLinkApp) return;
        if (!apps.some((app) => app.code === deepLinkApp)) return;
        toggleApp(deepLinkApp);
        setSearchParams(
            (prev) => {
                const next = new URLSearchParams(prev);
                next.delete('application');
                next.set('view', 'list');
                return next;
            },
            { replace: true }
        );
    }, [deepLinkApp, apps, toggleApp, setSearchParams]);

    // EAA-1846 (frame 95): breadcrumb moved to the shared topbar. Called
    // unconditionally (Rules of Hooks) — ahead of the capability early return
    // below, since a hook cannot follow one.
    useConsoleBreadcrumb([
        { label: t('console.roleMap.page.breadcrumbParent') },
        { label: t('console.roleMap.page.title') },
    ]);

    // The `application` deep link arrives before the list view's own queries
    // have been enabled, so read the capability gate rather than `enabled`.
    if (!listVm.canView) return null;

    const isBoard = view === 'board';
    const subtitle = isBoard
        ? t('console.roleMap.page.subtitleBoard', {
              appCount: boardVm.appCount,
              roleKeyCount: boardVm.roleKeyCount,
          })
        : t('console.roleMap.page.subtitleList', {
              totalLinks: listVm.totalLinks,
              appCount: listVm.appCount,
          });

    return (
        <main className="portal-admin portal-console-page portal-console-page--fit">
            <PageHeader
                title={t('console.roleMap.page.title')}
                subtitle={subtitle}
                actions={
                    <>
                        <Segmented
                            options={VIEW_OPTIONS}
                            value={view}
                            onChange={(next) => setView(next as RoleMapView)}
                            aria-label={t('console.roleMap.page.viewToggleAriaLabel')}
                        />

                        {/* EAA-1841: list-view only — not because the board cannot
                            sync, but because it already has its own sync button on
                            the editor card, scoped to the app being edited (frame
                            42). A second global one in the header would be two
                            buttons firing the same write on one screen. */}
                        {!isBoard && listVm.canSync && (
                            <Button
                                onClick={() => listVm.syncMutation.mutate()}
                                disabled={listVm.syncMutation.isPending}
                                title={t('console.roleMap.page.syncButtonTitle')}
                            >
                                {listVm.syncMutation.isPending
                                    ? t('console.roleMap.page.syncing')
                                    : t('console.roleMap.page.syncCatalog')}
                            </Button>
                        )}

                        {/* EAA-1837: CSV export reads the org-scoped queryset, so it
                            is a list-view action only — the board has neither a
                            scope control nor a row selection to export. */}
                        {!isBoard && (
                            <Button
                                onClick={() => void listVm.exportCsv()}
                                disabled={!listVm.exportEnabled || listVm.exportBusy}
                                title={
                                    listVm.exportEnabled
                                        ? t('console.roleMap.page.exportTitleEnabled')
                                        : t('console.roleMap.page.exportTitleDisabled')
                                }
                                icon={
                                    <svg
                                        viewBox="0 0 24 24"
                                        width="15"
                                        height="15"
                                        fill="none"
                                        stroke="currentColor"
                                        strokeWidth={1.8}
                                        aria-hidden="true"
                                    >
                                        <path
                                            d="M12 16V4m0 12 4-4m-4 4-4-4M4 20h16"
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                        />
                                    </svg>
                                }
                            >
                                {listVm.exportBusy
                                    ? t('console.roleMap.page.exporting')
                                    : t('console.roleMap.page.exportCsv')}
                            </Button>
                        )}

                        {/* EAA-1841: list-view only, and not merely for tidiness —
                            the modal behind it is driven by `useRoleMapTable`, whose
                            queries are off in the board view, so here it would offer
                            an empty app list. Design frame 42 has no global add
                            button either: the board edits through each role row's
                            own `+ vai trò` picker. */}
                        {!isBoard && (
                            <Button
                                type="primary"
                                onClick={listVm.openAdd}
                                disabled={!listVm.canManageCurrentScope}
                                icon={
                                    <svg
                                        viewBox="0 0 24 24"
                                        width="16"
                                        height="16"
                                        fill="none"
                                        stroke="currentColor"
                                        strokeWidth={2}
                                        aria-hidden="true"
                                    >
                                        <path d="M12 5v14M5 12h14" strokeLinecap="round" />
                                    </svg>
                                }
                            >
                                {t('console.roleMap.page.addLink')}
                            </Button>
                        )}
                    </>
                }
            />

            {/* EAA-1817: no `SectionCard` for the board — it draws its own
                multi-card layout (rail + coverage matrix + editor, each with its
                own ring), so wrapping it in one more card would put a card inside
                a card, the same reasoning `PermissionsPage`'s "roles" tab already
                documents for its own rail+editor pair. */}
            {isBoard ? (
                <div className="fit-grow">
                    <RoleMapBoardView vm={boardVm} />
                </div>
            ) : (
                <div className="fit-grow">
                    <SectionCard>
                        <RoleMapListView vm={listVm} />
                    </SectionCard>
                </div>
            )}
        </main>
    );
}

export default RoleMapPage;
